<script setup lang="ts">
import type { ButtonProps, ButtonVariants } from '/#/components/base/button/button'
import type { DropdownEmit, DropdownProps } from '/#/components/base/dropdown'
import type { IconProps } from '/#/components/base/icon/icon'
import type { ComputedRef, Ref } from 'vue'

import isUndefined from 'lodash-es/isUndefined'

import { BODY } from '/@/constants/common'
import { mergeIconProps } from '/@/utils/helper'

const props = withDefaults(defineProps<DropdownProps>(), {
  color: 'primary',
  variant: 'solid',
  position: 'bottom-end',
  tippyTheme: 'dropdown',
  theme: undefined,
  title: undefined,
  icon: undefined,
  options: undefined,
  list: undefined,
  iconColor: 'slate',
  offset: () => [0, 2],
})

const emit = defineEmits<DropdownEmit>()

const { t } = useI18n()

/**
 * @name isTriggerClick
 * @description If disabled is true, it changes the trigger to manual.
 * Because if the trigger is click, it will not be closed.
 */
const isTriggerClick: ComputedRef<string> = computed(() =>
  props.disabled ? 'manual' : 'click',
)

/**
 * @name tippyOptions
 * @description Tippy options for dropdown
 */
const tippyOptions = {
  placement: props.position,
  trigger: props.hover ? 'mouseenter' : isTriggerClick.value,
  arrow: props.arrow,
  offset: props.offset,
  duration: 200,
  appendTo: props.appendToBody ? BODY : undefined,
  theme: props.tippyTheme,
}

/**
 * @name appearance
 * @description Add classes for dropdown appearance
 */
const buttonProps: ComputedRef<ButtonProps> = computed(() => {
  if (props.buttonProps) {
    return props.buttonProps
  }

  return {
    color: props.color,
    size: props.size,
    variant: props.variant as ButtonVariants,
    disabled: props.disabled,
    noPadding: props.noPadding,
  }
})

/**
 * @name iconProps
 * @description Add classes for icon appearance
 */
const iconProps: ComputedRef<IconProps | undefined> = computed(() => {
  const defaultIconProps: Omit<IconProps, 'name'> = {
    color: props.iconColor,
    size: 'sm',
  }

  return mergeIconProps(defaultIconProps, props.icon)
})

/**
 * @name refList
 * @description Convert list a reactive variable for dynamic list
 */
const refList = toRef(props, 'list')

/**
 * @name selectedApproveItem
 * @description Holds selected item when approvable is true and emit select event when selected item
 */
const selectedApproveItem: Ref<string | number> = ref('')
/**
 * @name tippyWrapper
 * @description Holds tippy instance
 */
const tippyWrapper: Ref = ref()
/**
 * @name dropdownContent
 * @description Holds dropdown content element
 */
const dropdownContent: Ref<HTMLDivElement> = ref({} as HTMLDivElement)

/**
 * @method showDropdown
 * @returns void
 * @description Show dropdown programmatically.
 */
function showDropdown(): void {
  tippyWrapper.value.tippy.show()
}

// Hide dropdown programmatically.
/**
 * @method hideDropdown
 * @returns void
 * @description Hide dropdown programmatically.
 */
function hideDropdown(): void {
  tippyWrapper.value.tippy.hide()
}

/**
 * @method closeOptions
 * @param hide
 * @returns void
 * @description If it has afterClose props, it closes the dropdown after the option is selected
 */
function closeOptions(hide: Function): void {
  if (props.afterClose) hide()
}

/**
 * @method approveSelect
 * @param item
 * @returns void
 * @description If approvable is true, set selected item for approve button.
 * Otherwise, emit select event.
 */
function approveSelect(item: string): void {
  if (props.approvable) selectedApproveItem.value = item
  else emit('select', item)
}

/**
 * @method onHideTippy
 * @returns void
 * @description Emit onClose event when tippy hide.
 */
function onHideTippy() {
  emit('onClose')
}

/**
 * Expose dropdown functions.
 */
defineExpose({
  showDropdown,
  hideDropdown,
})
</script>

<template>
  <Tippy
    v-bind="tippyOptions"
    ref="tippyWrapper"
    tabindex="0"
    interactive
    :on-hide="onHideTippy"
  >
    <template #default="tippy">
      <div
        class="dropdown"
        :class="[theme]"
        tabindex="-1"
       
      >
        <slot name="button" v-bind="tippy">
          <!-- using just with icon has not arrow -->
          <Button
            v-if="variant === 'icon'"
            :icon="iconProps"
            circle
            tabindex="0"
            variant="link"
            :size="buttonProps.size"
            :disabled="buttonProps.disabled"
            :loading="showLoader"
           
            :aria-label="t('view-more-actions')"
          />

          <Button
            v-else-if="variant === 'button-with-icon'"
            v-bind="buttonProps"
            tabindex="0"
            :loading="showLoader"
           
            :aria-label="t('view-more-actions')"
          >
            <slot name="inside-button" />
            {{ buttonProps.text ?? buttonProps.text }}
          </Button>

          <!-- using with text. has arrow. this is default -->
          <Button
            v-else
            v-bind="buttonProps"
            :loading="showLoader"
            tabindex="0"
           
            :icon="{
              name: !tippy.state.isVisible ? 'chevron-down' : 'chevron-up',
              position: 'after',
            }"
          >
            <slot name="before-icon" />
            <Icon
              v-if="!isUndefined(iconProps)"
              :name="iconProps.name"
              :color="iconProps.color"
              :size="iconProps.size"
            />
            <span>{{ title }}</span>
          </Button>
        </slot>
      </div>
    </template>

    <template #content="tippy">
      <!-- TODO: dropdown - Remove this click event. Must be on the item @oktay -->
      <div
        ref="dropdownContent"
        class="dropdown-content"
        @keydown="closeOptions(tippy.hide)"
        @click="closeOptions(tippy.hide)"
      >
        <slot name="content" :hide="hideDropdown" v-bind="tippy">
          <template v-if="refList">
            <template v-for="item in refList" :key="item.value">
              <DropdownItem
                @click.prevent.stop="approveSelect(item.value as string)"
                @keypress.enter.prevent.stop="approveSelect(item.value as string)"
              >
                <LineApprove
                  :item-id="item.value as string"
                  :remove-item-id="selectedApproveItem"
                  inline-mode
                  @approve="emit('select', item.value as string)"
                />
                <a v-if="item.value !== selectedApproveItem">
                  {{ item?.label }}
                </a>
              </DropdownItem>
            </template>
          </template>
        </slot>
      </div>
    </template>
  </Tippy>
</template>
